import { Form } from '@mage-ui/components';
import { z } from 'zod';
import { validateSpanishId } from '../../../../utils/validateDocument';
import { DocumentTypeEnum, PriceTypeEnum, TeacherTypeEnum } from './enums';

const PHONE_REGEX = /^\d{9}$/u;
const ZIP_REGEX = /^\d{5}$/u;

export const rules = ({ st, priceType }) =>
  z
    .object({
      name: Form.InputText.rules.required(
        st('validation.required', { attribute: '' }),
      ),
      surname: Form.InputText.rules.required(
        st('validation.required', { attribute: '' }),
      ),
      gender: Form.Select.rules
        .required(st('validation.required', { attribute: '' }))
        .transform((val) => val?.id),
      occupation: Form.Select.rules
        .required(st('validation.required', { attribute: '' }))
        .transform((val) => val?.id),
      country: Form.InputText.rules.required(
        st('validation.required', { attribute: '' }),
      ),
      studied_duration: z.string().optional(),
      self_assessed_level: Form.InputText.rules.required(
        st('validation.required', { attribute: '' }),
      ),
      birthday: z.preprocess(
        (val) => {
          if (val === '' || val === null) {
            return undefined;
          }
          if (val instanceof Date) {
            return val;
          }
          const parsed = new Date(val);
          return Number.isNaN(parsed.getTime()) ? val : parsed;
        },
        z
          .date({
            required_error: st(
              'validation.required',
              { attribute: '' },
              'Aquest camp és obligatori',
            ),
            invalid_type_error: st('validation.invalid_date', {}),
          })
          .transform((val) => {
            const date = new Date(val);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          }),
      ),
      address: Form.InputText.rules.required(
        st('validation.required', { attribute: '' }),
      ),
      zip_code: Form.InputText.rules
        .required(st('validation.required', { attribute: '' }))
        .regex(ZIP_REGEX, {
          message: st('validation.regex', { attribute: '' }),
        }),
      city: Form.InputText.rules.required(
        st('validation.required', { attribute: '' }),
      ),
      document_type: Form.Select.rules
        .required(st('validation.required', { attribute: '' }))
        .transform((val) => val?.id),
      phone: Form.InputText.rules
        .required(st('validation.required', { attribute: '' }))
        .regex(PHONE_REGEX, {
          message: st('validation.regex', { attribute: '' }),
        }),
      tutor_phone: z
        .string()
        .optional()
        .nullable()
        .superRefine((val, ctx) => {
          if (val && !PHONE_REGEX.test(val)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: st('validation.regex', { attribute: '' }),
            });
          }
        }),

      tutor_email: z
        .string()
        .email({ message: st('validation.email', { attribute: '' }) })
        .optional()
        .nullable(),
      is_teacher: z.enum(['yes', 'no']).transform((val) => val === 'yes'),
      teacher_type: z
        .object({
          id: z.enum(Object.values(TeacherTypeEnum)),
          value: z.string(),
        })
        .optional()
        .nullable()
        .transform((val) => val?.id),
      identification_file: z
        .any()
        .refine((files) => Array.isArray(files) && files.length > 0, {
          message: st(
            'validation.required',
            { attribute: '' },
            'Aquest camp és obligatori',
          ),
        })
        .refine(
          (files) =>
            Array.isArray(files) &&
            files.every((file) => file.type === 'application/pdf'),
          st('validation.file_type', { attribute: '' }),
        ),
      email: Form.InputText.rules
        .required(st('validation.required', { attribute: '' }))
        .email({ message: st('validation.email', { attribute: '' }) }),

      email_confirmation: Form.InputText.rules
        .required(st('validation.required', { attribute: '' }))
        .email({ message: st('validation.email', { attribute: '' }) }),

      document_number: Form.InputText.rules.required(
        st('validation.required', { attribute: '' }),
      ),

      subsidy_id: z
        .object({
          id: z.string(),
          value: z.string(),
          percentage: z.number().optional(),
        })
        .optional()
        .nullable()
        .refine((val) => !!val?.id || priceType !== PriceTypeEnum.subsidized, {
          message: st(
            'validation.required',
            { attribute: '' },
            'Aquest camp és obligatori',
          ),
        })
        .transform((val) => val?.id),

      is_school_student: z
        .enum(['yes', 'no'])
        .transform((val) => val === 'yes'),
      has_studied_before: z
        .enum(['yes', 'no'])
        .transform((val) => val === 'yes'),
      discount_accreditation_file: z
        .any()
        .optional()
        .nullable()
        .refine(
          (files) =>
            !files ||
            (Array.isArray(files) &&
              files.every((file) => file.type === 'application/pdf')),
          st('validation.file_type', { attribute: '' }),
        ),
      level_accreditation_file: z
        .any()
        .optional()
        .nullable()
        .refine(
          (files) =>
            !files ||
            (Array.isArray(files) &&
              files.every((file) => file.type === 'application/pdf')),
          st('validation.file_type', { attribute: '' }),
        ),
      has_accepted_terms: z
        .boolean()
        .default(false)
        .refine((val) => val === true, {
          message: st(
            'validation.required',
            { attribute: '' },
            'Aquest camp és obligatori',
          ),
        }),
    })
    .superRefine((data, ctx) => {
      if (data.email !== data.email_confirmation) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: st('validation.confirmed', { attribute: '' }),
          path: ['email_confirmation'],
        });
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: st('validation.confirmed', { attribute: '' }),
          path: ['email'],
        });
      }

      if (data.document_type === DocumentTypeEnum.nif) {
        const isValid = validateSpanishId(data.document_number);

        if (!isValid) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: st(
              'validation.invalid_document_number',
              {
                attribute: '',
              },
              'El DNI no és vàlid',
            ),
            path: ['document_number'],
          });
        }
      }

      if (Number.parseInt(data.subsidy_id) > 1) {
        const files = data.discount_accreditation_file;

        if (!files || files.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: st(
              'validation.required',
              { attribute: '' },
              'Aquest camp és obligatori',
            ),
            path: ['discount_accreditation_file'],
          });
        }
      }
    });
